import { MultiMap } from "mnemonist";
import * as moment from "moment";
import IChannel, { iBroadcastHandler, iChannelSubscription } from "@/interfaces/channels/channel";
import Broadcast from "./broadcast";
import { iBroadcastConstructor } from "@/interfaces/channels/broadcast";

export default abstract class Channel implements IChannel {
    /**
     * A multi map of channel subscriptions.
     */
    protected subscribers: MultiMap<
        iBroadcastConstructor<any, any>,
        ChannelSubscription<any, Broadcast<any>>
    > = new MultiMap();

    /**
     * @inheritdoc
     */
    public abstract start(): void;

    /**
     * @inheritdoc
     */
    public abstract stop(): void;

    /**
     * @inheritdoc
     */
    public subscribe<D, B extends Broadcast<D>>(
        broadcast: iBroadcastConstructor<D, B>,
        handler: iBroadcastHandler<D, B>
    ): iChannelSubscription<D, B> {
        const subscription = new ChannelSubscription(handler);
        this.subscribers.set(broadcast, subscription);
        return subscription;
    }

    /**
     * Publishes a broadcast on this channel.
     *
     * @param abstract The broadcast constructor.
     * @param data The data to broadcast.
     */
    protected publish<D, B extends Broadcast<D>>(abstract: iBroadcastConstructor<D, B>, data: D): void {
        const subscribers = this.subscribers.get(abstract) || [];
        const concrete = new abstract(data);

        subscribers.forEach(subscriber => {
            try {
                subscriber.handle(concrete)
            } catch (err) {
                if (!(this as any).report) {
                    console.error(err);
                } else {
                    (this as any).report(err);
                }
            }
        });
    }
}

class ChannelSubscription<D, B extends Broadcast<D>> implements iChannelSubscription<D, B> {
    /**
     * Whether the subscription is active;
     */
    protected active: boolean = true;

    /**
     * An array of callbacks to call after handling the broadcast.
     */
    protected afters: Array<() => void> = [];

    /**
     * An array of callbacks to call once after handling the broadcast.
     */
    protected afterOnces: Array<() => void> = [];

    /**
     * An array of callbacks to call before handling the broadcast.
     */
    protected befores: Array<() => void> = [];

    /**
     * An array of callbacks to call once before handling the broadcast.
     */
    protected beforesOnces: Array<() => void> = [];

    /**
     * The broadcast handler.
     */
    protected handler: iBroadcastHandler<D, B>;

    /**
     * When the subscription was last handled.
     */
    protected lastHandledAt: moment.Moment | null;

    public constructor(handler: iBroadcastHandler<D, B>) {
        this.lastHandledAt = null;
        this.handler = handler;

        // Clear the once callbacks.
        this.after(() => {
            this.beforesOnces = [];
            this.afterOnces = [];
        });
    }

    /**
     * @inheritdoc
     */
    public handle(broadcast: B): void {
        if (!this.active) {
            return;
        }

        this.callActions(this.beforesOnces);
        this.callActions(this.befores);

        this.handler(broadcast);

        this.callActions(this.afterOnces);
        this.callActions(this.afters);
    }

    /**
     * @inheritdoc
     */
    public once(): iChannelSubscription<D, B> {
        if (this.lastHandledAt !== null) {
            this.unsubscribe();
        }

        this.after(() => this.unsubscribe(), true);

        return this;
    }

    /**
     * @inheritdoc
     */
    public unsubscribe(): void {
        this.active = false;
    }

    /**
     * Calls a callback after handling a broadcast.
     *
     * @param callback The callback to call after handling a broadcast.
     * @param once Whether the only apply this callback once.
     */
    protected after(callback: () => void, once: boolean = false): void {
        once ? this.afterOnces.push(callback) : this.afters.push(callback);
    }

    /**
     * Calls a callback before handling a broadcast.
     *
     * @param callback The callback to call before handling a broadcast.
     * @param once Whether the only apply this callback once.
     */
    protected before(callback: () => void, once: boolean = false): void {
        once ? this.beforesOnces.push(callback) : this.befores.push(callback);
    }

    /**
     * Calls some callbacks.
     *
     * @param callbacks The callbacks to call.
     */
    protected callActions(callbacks: Array<() => void>): void {
        callbacks.forEach(callback => callback());
    }
}